// React & MUI
import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";

// Internal
import { DialogComponent } from "@components/Dialog";
import { FormProvider } from "@components/hook-form";
import { useOdooConnectForm } from "../../hooks/useOdooConnectForm";
import { AskDostOdooConnectDialogActions } from "./AskDostOdooConnectDialogActions";
import { AskDostOdooConnectDialogContent } from "./AskDostOdooConnectDialogContent";

// Styles
import { getIconBadgeStyle } from "../../styles/askDostStyles";
import OdooLogo from "src/assets/logo/Odoo.png";

// Types
import { ISyncedEntity } from "../../types";

export interface IAskDostOdooConnectDialogProps {
  open: boolean;
  onClose: () => void;
  translate: (text: string, options?: any) => string;
  onConnectStart?: (retry: () => void) => void;
  onConnectResult?: (entities: ISyncedEntity[] | null) => void;
}

export const AskDostOdooConnectDialog: React.FC<
  IAskDostOdooConnectDialogProps
> = ({ open, onClose, translate, onConnectStart, onConnectResult }) => {
  const { methods, isSubmitting, handleClose, handleConnect } =
    useOdooConnectForm({ translate, onClose, onConnectStart, onConnectResult });

  const theme = useTheme();

  const customDialogHeader = (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          ...getIconBadgeStyle(theme.palette, { variant: "bordered" }),
          flexShrink: 0,
        }}
      >
        <img
          src={OdooLogo}
          alt="Odoo"
          width={28}
          style={{ display: "block" }}
        />
      </Box>

      <Stack spacing={0.25}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "1.1rem",
            color: theme.palette.text.primary,
          }}
        >
          {translate("connect_odoo")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 400,
            lineHeight: 1.4,
          }}
        >
          {translate("odoo_connect_subtitle")}
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <FormProvider methods={methods}>
      <DialogComponent
        isShowModal={open}
        showModalMethod={handleClose}
        dialogTitle={customDialogHeader as any}
        dialogSubTitle=""
        maxWidth="xs"
        fullWidth
        dialogContent={
          <AskDostOdooConnectDialogContent translate={translate} />
        }
        dialogActions={
          <AskDostOdooConnectDialogActions
            translate={translate}
            isSubmitting={isSubmitting}
            onCancel={handleClose}
            onConnect={handleConnect}
          />
        }
      />
    </FormProvider>
  );
};

